import { useRef } from 'react';

/**
 * Hook which returns a stable value based on the result of the provided factory
 * on the first render.
 *
 * This hook is similar to `useMemo` with an empty dependency array. However,
 * `useMemo` may re-evaluate its held value even when the dependencies have not
 * changed.
 *
 * This implementation only ever invokes the factory function once per mount (on
 * the first render), even if the component is re-rendered prior to the commit
 * phase.
 */
export const useInitialValue = <T>(factory: () => T) => {
  const ref = useRef<T>();

  // Reading or writing refs during render is generally prohibited, except for
  // "code like `if (!ref.current) ref.current = new Thing()` which only sets
  // the ref once during the first render". See:
  // https://beta.reactjs.org/learn/referencing-values-with-refs#best-practices-for-refs
  if (!ref.current) {
    ref.current = factory();
  }

  return ref.current;
};
